import { makeAutoObservable } from 'mobx';
import isNil from 'lodash/isNil';

import { B2B } from '@/b2b/entity/B2B';
import { DriveItemType } from '@/drive/entities/DriveItem/DriveItem';
import { PinnedCarbonProject } from '@/forwarding/entities/PinnedItem/PinnedCarbonProject';
import { createEmptyForm } from '@/forwarding/types/ForwardForm';
import { ForwardInfo } from '@/forwarding/types/ForwardInfo';
import { DriveProjectSummary } from '@/multistage/entities/DriveProjectSummary';
import { GlobalAccessLevel } from '@/security/constants/GlobalAccessLevel';
import { Contact, ContactModel } from '@/security/entities/Contact';
import { GlobalAccessTarget } from '@/security/entities/GlobalAccessTarget';
import { ContactAccessInfo } from '@/security/types/ContactAccessInfo';
import { ProjectDataAccess } from '@/security/types/DataAccess';

import Solution from './Solution';

interface ProjectNextPayment {
  amount: string;
  days: number;
  type: number;
}

export interface ProjectModel {
  id: string;
  title: string;
  type: number;
  exportedAt: string;
  createdAt: string;
  weight: number;
  solutionsCount: Nullable<number>;
  maxSolutionDiamonds: number;
  isLabGrown: boolean;
  maxYield: Nullable<number>;
  access: GlobalAccessLevel;
  dataAccess: ProjectDataAccess;
  b2b: Nullable<B2B>;
  file: Nullable<string>;
  cbr: Nullable<string>;
  expenses?: string;
  next?: ProjectNextPayment;
  acl?: { dataAccess: ProjectDataAccess; assignee: ContactModel }[];
  conferenceURL?: string;
  shortView?: string;

  _embedded?: {
    driveProject?: DriveProjectSummary;
    cbr?: string;
    tasks?: {
      maxAvailableAt: string;
    };
  };
}

export default class Project {
  id: string;

  title: string;

  type: number;

  exportedAt: string;

  createdAt: string;

  weight: number;

  solutionsCount: Nullable<number>;

  maxSolutionDiamonds: number;

  isLabGrown: boolean;

  maxYield: Nullable<number>;

  access: GlobalAccessLevel;

  dataAccess: ProjectDataAccess;

  acl: ContactAccessInfo[] = [];

  b2b: Nullable<B2B>;

  file: Nullable<string>;

  cbr: Nullable<string>;

  solutions: Solution[] = [];

  expenses?: string;

  next?: ProjectNextPayment;

  shortView: string;

  driveProject: Nullable<DriveProjectSummary> = null;

  tasksAvailableAt: Nullable<string> = null;

  constructor({
    id,
    title,
    type,
    exportedAt,
    createdAt,
    file,
    weight,
    solutionsCount,
    maxYield,
    maxSolutionDiamonds,
    b2b,
    access,
    dataAccess,
    cbr,
    isLabGrown = false,
    expenses,
    next,
    acl,
    shortView,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    _embedded,
  }: ProjectModel) {
    makeAutoObservable(this, {
      id: false,
      title: false,
      type: false,
      exportedAt: false,
      createdAt: false,
      weight: false,
      maxSolutionDiamonds: false,
      isLabGrown: false,
      maxYield: false,
      b2b: false,
      file: false,
      cbr: false,
      expenses: false,
      next: false,
      shortView: false,
      driveProject: false,
    });

    this.id = id;
    this.title = title;
    this.type = type;
    this.exportedAt = exportedAt;
    this.createdAt = createdAt;
    this.file = file;
    this.weight = weight;
    this.solutionsCount = !isNil(solutionsCount) ? solutionsCount : null;
    this.maxYield = !isNil(maxYield) ? maxYield : null;
    this.cbr = cbr;
    this.isLabGrown = isLabGrown;
    this.maxSolutionDiamonds = maxSolutionDiamonds;
    this.b2b = b2b ? new B2B(b2b) : null;
    this.access = access;
    this.dataAccess = dataAccess;
    this.expenses = expenses;
    this.next = next;
    this.acl = (acl ?? []).map((info) => ({ dataAccess: info.dataAccess, assignee: new Contact(info.assignee) }));
    this.shortView = shortView ?? '';
    this.driveProject = _embedded?.driveProject ?? null;
    this.tasksAvailableAt = _embedded?.tasks?.maxAvailableAt ?? null;
  }

  get products() {
    if (!this.solutions) {
      return null;
    }

    if (this.solutions.length === 0) {
      return [];
    }

    return this.solutions.map((s) => s.products).flat();
  }

  toForwardInfo(): ForwardInfo {
    return {
      preview: { type: DriveItemType.CarbonProject },
      ...createEmptyForm(),
      title: this.title,
    };
  }

  toPinnedItem(): PinnedCarbonProject {
    return new PinnedCarbonProject(this);
  }

  toGlobalAccessTarget(): GlobalAccessTarget {
    return new GlobalAccessTarget({
      id: this.id,
      access: this.access,
      dataAccess: this.dataAccess,
      hasDataAccess: true,
    });
  }

  setSolutions(solutions: Solution[]) {
    this.solutions = solutions;
  }

  setAccess = (access: Any) => {
    this.access = access;
  };

  getParentId() {
    const projectSummary = this.driveProject;

    if (!projectSummary?.project || projectSummary.suggest) {
      return null;
    }

    return projectSummary.project.id;
  }
}
